import React, { useMemo } from 'react';
import { stripLeadingDecorators } from './types';
import { MarkdownInline } from './MarkdownInline';
import { TableBlock } from './TableBlock';

interface MarkdownRendererProps {
    content: string;
}

type Block =
    | { kind: 'h3'; text: string; key: string }
    | { kind: 'p'; text: string; key: string }
    | { kind: 'list'; items: string[]; key: string }
    | { kind: 'table'; lines: string[]; key: string };

type Section = {
    key: string;
    title: string | null;
    blocks: Block[];
};

const BODY = 'text-pretty text-base leading-relaxed text-secondary';
const CARD =
    `rounded-xl border border-subtle bg-surface-1 px-s4 py-s4 sm:px-s5 sm:py-s5 ${BODY}`;

function parseSections(content: string): Section[] {
    const sections: Section[] = [];
    let current: Section = { key: 'section-intro', title: null, blocks: [] };
    let listItems: string[] = [];
    let tableLines: string[] = [];
    let key = 0;

    const pushSection = () => {
        if (current.title !== null || current.blocks.length > 0) {
            sections.push(current);
        }
    };

    const flushList = () => {
        if (listItems.length === 0) return;
        current.blocks.push({ kind: 'list', items: listItems, key: `list-${key++}` });
        listItems = [];
    };

    const flushTable = () => {
        if (tableLines.length === 0) return;
        current.blocks.push({ kind: 'table', lines: tableLines, key: `table-${key++}` });
        tableLines = [];
    };

    for (const line of content.split('\n')) {
        const trimmed = line.trim();

        if (trimmed.startsWith('|')) {
            flushList();
            tableLines.push(line);
            continue;
        }

        if (tableLines.length > 0) flushTable();

        if (trimmed === '') {
            flushList();
            continue;
        }

        const headingMatch = trimmed.match(/^(#{1,6})\s+(.*)$/);
        if (headingMatch) {
            flushList();
            const text = stripLeadingDecorators(headingMatch[2]);
            if (!text) continue;

            if (headingMatch[1].length <= 2) {
                pushSection();
                current = { key: `h-${key++}`, title: text, blocks: [] };
            } else {
                current.blocks.push({ kind: 'h3', text, key: `h-${key++}` });
            }
            continue;
        }

        if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
            listItems.push(trimmed.replace(/^[-*]\s+/, ''));
            continue;
        }

        flushList();
        current.blocks.push({
            kind: 'p',
            text: stripLeadingDecorators(trimmed),
            key: `p-${key++}`,
        });
    }

    flushList();
    flushTable();
    pushSection();
    return sections;
}

function renderBlock(block: Block) {
    if (block.kind === 'h3') {
        return (
            <h4
                key={block.key}
                className="text-base font-semibold tracking-tight text-default"
            >
                {block.text}
            </h4>
        );
    }

    if (block.kind === 'p') {
        return (
            <p key={block.key} className={BODY}>
                <MarkdownInline text={block.text} />
            </p>
        );
    }

    if (block.kind === 'list') {
        return (
            <ul
                key={block.key}
                className="m-0 flex list-none flex-col gap-s5 p-0 sm:gap-s6"
            >
                {block.items.map((item, i) => (
                    <li key={`${block.key}-${i}`} className={CARD}>
                        <MarkdownInline text={item} />
                    </li>
                ))}
            </ul>
        );
    }

    return <TableBlock key={block.key} lines={block.lines} />;
}

export const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ content }) => {
    const sections = useMemo(() => parseSections(content), [content]);

    return (
        <article
            aria-label="Relatório de análise de vendas"
            className="flex flex-col gap-s8 sm:gap-s10"
        >
            {sections.map((section) => (
                <section
                    key={section.key}
                    aria-labelledby={section.title ? section.key : undefined}
                    className="flex flex-col gap-s4 sm:gap-s5"
                >
                    {section.title ? (
                        <header className="flex items-center gap-s3">
                            <span
                                className="h-5 w-0.5 shrink-0 rounded-full bg-primary"
                                aria-hidden
                            />
                            <h3
                                id={section.key}
                                className="text-balance text-md font-semibold tracking-tight text-default sm:text-lg"
                            >
                                {section.title}
                            </h3>
                        </header>
                    ) : null}
                    {section.blocks.map(renderBlock)}
                </section>
            ))}
        </article>
    );
};
